import { Vector3, Object3D, Quaternion } from 'three';
import { IPanControl, IPanArguments } from './pan-control-interface';

/**
 * projection mode agnostic object panner
 *
 * [live demo](https://threejs.org/examples/misc_controls_trackball.html)
 *
 * example:
 * ```typescript
 * const camera = new Quick3DOrthographicCamera();
 * const panControl = new GenericPanControl();
 * // translate the camera along its local x-axis by 50 units
 * panControl.pan(camera, {normalizedDirectionX: 1, panSpeed: 50});
 * // translate the camera along its local y-axis by 20 units
 * panControl.pan(camera, {normalizedDirectionY: 1, panSpeed: 20});
 * // perform both of the above steps in a single call
 * panControl.pan(camera, {normalizedDirectionX: 50, normalizedDirectionY: 20});
 * ```
 */
export class GenericPanControl implements IPanControl {
    private localX: Vector3;
    private localY: Vector3;
    private localZ: Vector3;

    public constructor() {
        this.localX = new Vector3();
        this.localY = new Vector3();
        this.localZ = new Vector3();
    }

    /**
     * translates the object in 3D space along its local x and y axes
     * @param panArguments.normalizedDirectionX defaulted to 0
     * @param panArguments.normalizedDirectionY defaulted to 0
     * @param panArguments.panSpeed defaulted to 1. Negative values move the object backwards
     */
    public pan(object: Object3D, panArguments: IPanArguments): Vector3 {
        const {
            normalizedDirectionX = 0,
            normalizedDirectionY = 0,
            panSpeed = 1,
        } = panArguments;

        panArguments.panSpeed = 0;

        object.matrix.extractBasis(this.localX, this.localY, this.localZ);
        const deltaX = this.localY.multiplyScalar(normalizedDirectionY * panSpeed);
        const deltaY = this.localX.multiplyScalar(normalizedDirectionX * panSpeed);
        object.position
            .add(deltaX)
            .add(deltaY);

        const delta = new Vector3();
        delta.applyQuaternion(
            object.getWorldQuaternion(new Quaternion()).invert()
        );
        object.position.add(delta);
        object.updateMatrix();
        return delta.add(deltaX).add(deltaY);
    }
}
